import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { getMe, getMyCompany, getPermissions, logout } from "../lib/requests";
import { useDispatch, useSelector } from "react-redux";
import { setUser, clearUser } from "../redux/auth/authSlice";
import { setCompany, clearCompany } from "../redux/company/companySlice";
import { useHistory } from "react-router-dom";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const company = useSelector((state) => state.company.company);
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const effectRan = useRef(false);

  const clearAuthData = useCallback(async () => {
    try {
      // Limpa os cookies HttpOnly no servidor
      await logout();
    } catch (_) {
      // ignora erros de logout (sessão já expirada, etc.)
    }
    dispatch(clearUser());
    dispatch(clearCompany());
    setIsAuthenticated(false);
  }, [dispatch]);

  const setAuthStatus = useCallback(
    (authStatus, userData = null, companyData = null) => {
      setIsAuthenticated(authStatus);
      if (authStatus) {
        if (userData) dispatch(setUser(userData));
        if (companyData) dispatch(setCompany(companyData));
        else dispatch(clearCompany());
      } else {
        dispatch(clearUser());
        dispatch(clearCompany());
      }
    },
    [dispatch]
  );

  const PUBLIC_PATHS = ["/pages/landing", "/pages/acesso-negado"];

  useEffect(() => {
    const controller = new AbortController();

    const initAuth = async () => {
      if (effectRan.current === true) return;
      effectRan.current = true;

      // Páginas públicas não precisam verificar sessão — evita chamadas 401
      if (PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p))) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // O cookie HttpOnly é enviado automaticamente pelo browser.
        // Se getMe retornar 200 a sessão está ativa; se 401 não está.
        const meResponse = await getMe();

        if (meResponse?.error) {
          // Sem sessão válida — deixa o PrivateRoute redirecionar
          dispatch(clearUser());
          dispatch(clearCompany());
          setIsAuthenticated(false);
          return;
        }

        const userData = meResponse.data;

        // Superadmin tem acesso irrestrito
        if (!userData.is_superuser) {
          // Verifica se o usuário tem acesso ao AllNube (sistema_id=3)
          let sistemas = [];
          try {
            const permResponse = await getPermissions();
            sistemas = permResponse?.data?.sistemas ?? [];
          } catch (_) {
            // Falha ao obter permissões — bloqueia por segurança
          }
          const hasAllnube = sistemas.some((s) => s.sistema_id === 3);
          if (!hasAllnube) {
            // Redireciona para tela de acesso negado mostrando sistemas disponíveis
            dispatch(setUser(userData));
            setIsAuthenticated(true);
            history.push("/pages/acesso-negado", { sistemas });
            return;
          }
        }

        let companyData = null;
        try {
          const companyResponse = await getMyCompany(userData.id);
          companyData = companyResponse.data;
        } catch (_) {}

        setAuthStatus(true, userData, companyData);
      } catch (_) {
        dispatch(clearUser());
        dispatch(clearCompany());
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      controller.abort();
    };
  }, [history]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated: setAuthStatus,
        clearAuthData,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
